import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, tokenStore } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bulk customer import dialog. Two-step UX:
 *  1) Download the CSV template (headers + 2 example rows)
 *  2) Upload the filled CSV/XLSX file — result shows created + errors
 *
 * Props: open, onOpenChange, onImported (fn — called after successful import)
 */
export function ImportCustomersDialog({ open, onOpenChange, onImported }) {
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setUploading(false);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const close = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const token = tokenStore.get();
      const resp = await fetch(`${API}/admin/import/deals-template`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("Failed to download");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vanguard-beacon-customer-import-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch {
      toast.error("Failed to download template");
    } finally {
      setDownloading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/import/deals", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created_count > 0) {
        toast.success(`Imported ${data.created_count} customer${data.created_count === 1 ? "" : "s"}`);
        onImported?.(data);
      } else {
        toast.error("No customers were imported — check the errors below");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="import-customers-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-brand-navy flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-brand-orange" /> Bulk Import Customers
          </DialogTitle>
          <DialogDescription>
            Import multiple customers into the pipeline from a CSV or Excel file.
            Start by downloading the template to make sure your columns line up.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 */}
        <section className="bg-brand-cream/50 border border-brand-navy/10 rounded-2xl p-5 mt-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-brand-orange font-semibold">Step 1</p>
          <h3 className="mt-1 text-base font-semibold text-brand-navy">Download the template</h3>
          <p className="mt-1 text-xs text-brand-slate/70">
            Excel workbook with locked headers, empty rows for you to fill,
            and built-in dropdowns for Subscription Plan, Status and PIC so
            you can't accidentally enter invalid values.
          </p>
          <button
            onClick={downloadTemplate}
            disabled={downloading}
            className="mt-3 inline-flex items-center gap-2 border border-brand-navy/20 bg-white text-brand-navy px-4 py-2 text-xs font-semibold rounded-full hover:bg-brand-cream disabled:opacity-60"
            data-testid="import-download-template"
          >
            <Download size={14} /> {downloading ? "Preparing…" : "Download Excel template"}
          </button>
        </section>

        {/* Step 2 */}
        <section className="mt-4 border border-brand-navy/10 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-brand-orange font-semibold">Step 2</p>
          <h3 className="mt-1 text-base font-semibold text-brand-navy">Upload your filled file</h3>
          <p className="mt-1 text-xs text-brand-slate/70">
            Accepted formats: <code>.csv</code>, <code>.xlsx</code>. Max 5 MB.
            Each row becomes a customer with its primary contact. Duplicate emails are merged.
          </p>

          <div className="mt-4 flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFile}
              disabled={uploading}
              className="text-sm file:mr-3 file:rounded-full file:border-0 file:bg-brand-orange file:text-white file:px-4 file:py-2 file:text-xs file:font-semibold file:cursor-pointer hover:file:bg-brand-orange-hover text-brand-slate"
              data-testid="import-file-input"
            />
            {uploading && <span className="text-xs text-brand-slate/60 inline-flex items-center gap-2"><Upload size={12} /> Uploading…</span>}
          </div>
        </section>

        {/* Column reference */}
        <section className="mt-4">
          <details className="text-xs text-brand-slate/70">
            <summary className="cursor-pointer text-brand-navy font-semibold text-sm">Required columns</summary>
            <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 list-disc pl-5">
              <li><strong>Customer Name</strong> — required</li>
              <li><strong>Subscription Plan</strong> — Essential / Pro / Enterprise (dropdown)</li>
              <li>Service Group — Catering / Food Hygiene Inspection / FSSC / FSSC Consultancy / GMP / HACCP / ISO Consultancy / SFA License (optional dropdown)</li>
              <li><strong>Contract Value SGD</strong> — number, no currency symbol</li>
              <li><strong>Status</strong> — New / Qualified / Discovery / Proposal / Negotiation / Won / Lost (dropdown)</li>
              <li>PIC — team member name (dropdown; blank = you)</li>
              <li>Expected Close Date — YYYY-MM-DD</li>
              <li>Notes — free text</li>
              <li>Contact Name / Email / Phone / Title</li>
            </ul>
          </details>
        </section>

        {/* Result */}
        {result && (
          <section className="mt-4 border border-brand-navy/10 rounded-2xl p-5" data-testid="import-result">
            <div className="flex items-center gap-2 mb-3">
              {result.failed_count === 0
                ? <CheckCircle2 size={18} className="text-emerald-600" />
                : <AlertTriangle size={18} className="text-amber-600" />}
              <h3 className="text-base font-semibold text-brand-navy">Result</h3>
            </div>
            <p className="text-sm text-brand-slate">
              Total rows: <strong>{result.total_rows}</strong> ·{" "}
              <span className="text-emerald-700 font-semibold" data-testid="import-created-count">
                {result.created_count} imported
              </span>
              {result.failed_count > 0 && (
                <> · <span className="text-red-700 font-semibold" data-testid="import-failed-count">{result.failed_count} failed</span></>
              )}
            </p>

            {result.errors?.length > 0 && (
              <div className="mt-3 max-h-52 overflow-y-auto rounded-xl border border-red-200 bg-red-50 p-3 text-xs">
                <p className="font-semibold text-red-800 mb-1">Errors</p>
                <ul className="space-y-1">
                  {result.errors.map((e) => (
                    <li key={e.row} className="text-red-800" data-testid={`import-error-row-${e.row}`}>
                      Row {e.row}: {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => close(false)}
            className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-brand-cream rounded-full transition-colors"
            data-testid="import-close-button"
          >
            {result ? "Done" : "Close"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
